export const STATIC_PAGE_CONTENT = {
  about: {
    title: "About Us",
    excerpt:
      "Homevilas is the curated hospitality platform of Northern Adventure Private Limited, built to make villa stays, homestays, and destination-led travel more personal, trusted, and memorable.",
    html: `
      <section>
        <p class="eyebrow">Our Story</p>
        <h2>A hospitality brand built around <em>trusted stays</em></h2>
        <p>
          Homevilas operates under <strong>Northern Adventure Private Limited</strong>, incorporated in New Delhi on
          <strong>18 February 2015</strong>. We started with a clear belief: travelers want more than rooms. They want
          privacy, warmth, local character, and dependable support before and during every trip.
        </p>
        <p>
          That belief shaped Homevilas into a curated platform for villas, luxury homestays, holiday packages, and premium
          travel services across India. Our focus has always been selective quality, transparent communication, and concierge-led
          support.
        </p>
      </section>

      <div class="hv-static-grid hv-static-grid-4">
        <div class="hv-static-stat-card">
          <div class="stat-value">2015</div>
          <div class="stat-label">Year founded</div>
        </div>
        <div class="hv-static-stat-card">
          <div class="stat-value">500+</div>
          <div class="stat-label">Verified properties</div>
        </div>
        <div class="hv-static-stat-card">
          <div class="stat-value">50+</div>
          <div class="stat-label">Destinations</div>
        </div>
        <div class="hv-static-stat-card">
          <div class="stat-value">24/7</div>
          <div class="stat-label">Guest support</div>
        </div>
      </div>

      <section>
        <h2>What makes Homevilas different</h2>
        <div class="hv-static-grid hv-static-grid-2">
          <div class="hv-static-card">
            <h3>Curated quality</h3>
            <p>We aim to feature stays that meet clear expectations around comfort, design, location, and hospitality.</p>
          </div>
          <div class="hv-static-card">
            <h3>Guest-first support</h3>
            <p>From enquiry to checkout, our team helps with planning, availability, and on-trip coordination.</p>
          </div>
          <div class="hv-static-card">
            <h3>Local character</h3>
            <p>We prefer properties that offer a strong sense of place instead of generic accommodation experiences.</p>
          </div>
          <div class="hv-static-card">
            <h3>Transparent trust</h3>
            <p>Clear communication, responsive assistance, and practical travel guidance remain central to our platform.</p>
          </div>
        </div>
      </section>

      <section>
        <h2>How we have grown</h2>
        <div class="hv-static-timeline">
          <div class="hv-static-timeline-item">
            <h3>2015</h3>
            <p>Homevilas launches as part of Northern Adventure Private Limited in New Delhi.</p>
          </div>
          <div class="hv-static-timeline-item">
            <h3>2016 to 2018</h3>
            <p>Expansion into curated villas and homestays across North India, followed by entry into Goa and South India.</p>
          </div>
          <div class="hv-static-timeline-item">
            <h3>2019 to 2022</h3>
            <p>Concierge support, stronger host verification, and broader stay categories are added to the platform.</p>
          </div>
          <div class="hv-static-timeline-item">
            <h3>2023 onwards</h3>
            <p>Homevilas continues building a stronger nationwide stay and travel marketplace with curated guest experiences.</p>
          </div>
        </div>
      </section>

      <section>
        <h2>Company information</h2>
        <div class="hv-static-callout">
          <p><strong>Registered Entity:</strong> Northern Adventure Private Limited</p>
          <p><strong>Operating Brand:</strong> Homevilas</p>
          <p><strong>Registered Office:</strong> E-6, Kalkaji, New Delhi-110019</p>
          <p><strong>Website:</strong> <a href="https://www.homevilas.com">www.homevilas.com</a></p>
        </div>
      </section>
    `,
  },
  contact: {
    title: "Contact Us",
    excerpt:
      "Reach the Homevilas support team for bookings, stay coordination, partnerships, and host onboarding through email, phone, or WhatsApp.",
    html: `
      <section>
        <p class="eyebrow">Get in touch</p>
        <h2>We are here to <em>help</em></h2>
        <p>
          Whether you need booking support, partnership assistance, package guidance, or host onboarding help, the Homevilas team
          is available across email, phone, and WhatsApp.
        </p>
      </section>

      <div class="hv-static-grid hv-static-grid-3">
        <div class="hv-static-card">
          <h3>Email support</h3>
          <p><a href="mailto:info@homevilas.com">info@homevilas.com</a></p>
          <p><a href="mailto:meena@homevilas.com">meena@homevilas.com</a></p>
          <p class="muted">Best for booking help, package enquiries, and general support.</p>
        </div>
        <div class="hv-static-card">
          <h3>Call or WhatsApp</h3>
          <p><a href="tel:+918287703118">+91 82877 03118</a></p>
          <p><a href="https://wa.me/918287703118" target="_blank" rel="noreferrer">Open WhatsApp chat</a></p>
          <p class="muted">Share your booking reference for faster support.</p>
        </div>
        <div class="hv-static-card">
          <h3>Office address</h3>
          <p>E-6, Kalkaji, New Delhi-110019</p>
          <p class="muted">Northern Adventure Private Limited, operating as Homevilas.</p>
        </div>
      </div>

      <section>
        <h2>Support availability</h2>
        <div class="hv-static-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Channel</th>
                <th>Availability</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Email support</td>
                <td>Daily, with response priority on active bookings</td>
              </tr>
              <tr>
                <td>WhatsApp support</td>
                <td>Recommended for urgent travel coordination</td>
              </tr>
              <tr>
                <td>Phone support</td>
                <td>Best for immediate assistance during business hours</td>
              </tr>
              <tr>
                <td>Host and partner enquiries</td>
                <td>Handled by the relevant onboarding or business team</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2>What to include in your message</h2>
        <ul>
          <li>Your booking reference, if applicable</li>
          <li>Stay dates, destination, or package name</li>
          <li>The issue or request in clear detail</li>
          <li>Preferred callback number or reply email</li>
        </ul>
      </section>

      <section>
        <h2>Useful links</h2>
        <div class="hv-static-link-list">
          <a href="/help">Visit Help Center</a>
          <a href="/partner-with-us">Partner with Us</a>
          <a href="/b2b-registration">B2B Registration</a>
          <a href="/become-a-host">Become a Host</a>
        </div>
      </section>
    `,
  },
  careers: {
    title: "Careers",
    excerpt:
      "Join Homevilas across hospitality operations, guest experience, partnerships, marketing, product, and travel support roles.",
    html: `
      <section>
        <p class="eyebrow">Work with us</p>
        <h2>Build hospitality that feels <em>personal</em></h2>
        <p>
          Homevilas brings together curated stays, guest support, host partnerships, and destination-led travel.
          We look for people who care about dependable service, clear communication, and thoughtful travel experiences.
        </p>
      </section>

      <div class="hv-static-grid hv-static-grid-3">
        <div class="hv-static-card">
          <h3>Guest experience</h3>
          <p>Support bookings, stay coordination, itinerary help, and guest communication across destinations.</p>
        </div>
        <div class="hv-static-card">
          <h3>Operations</h3>
          <p>Work with hosts, vendors, and local teams to keep stay experiences reliable and well coordinated.</p>
        </div>
        <div class="hv-static-card">
          <h3>Growth and product</h3>
          <p>Help improve listings, partnerships, digital journeys, and marketing for curated travel demand.</p>
        </div>
      </div>

      <section>
        <h2>Apply or enquire</h2>
        <div class="hv-static-callout">
          <p><strong>Email:</strong> <a href="mailto:info@homevilas.com">info@homevilas.com</a></p>
          <p><strong>Phone:</strong> <a href="tel:+918287703118">+91 82877 03118</a></p>
          <p>Please include your role interest, current city, resume or profile link, and earliest availability.</p>
        </div>
      </section>
    `,
  },
  help: {
    title: "Help Center",
    excerpt:
      "Find support for Homevilas bookings, payments, account access, property enquiries, cancellations, and stay coordination.",
    html: `
      <section>
        <p class="eyebrow">Support</p>
        <h2>Help for bookings, stays, and <em>travel plans</em></h2>
        <p>
          Use this page for common Homevilas support needs. For active bookings or urgent stay coordination,
          WhatsApp or phone support is usually the fastest channel.
        </p>
      </section>

      <div class="hv-static-grid hv-static-grid-2">
        <div class="hv-static-card">
          <h3>Bookings and availability</h3>
          <p>Share destination, travel dates, guest count, and preferred stay type so the team can guide you quickly.</p>
        </div>
        <div class="hv-static-card">
          <h3>Payments and confirmations</h3>
          <p>Include your payment reference, booking ID, registered phone number, and screenshot if available.</p>
        </div>
        <div class="hv-static-card">
          <h3>Cancellations and changes</h3>
          <p>Cancellation and date-change eligibility depends on the property policy shown during booking.</p>
        </div>
        <div class="hv-static-card">
          <h3>On-trip support</h3>
          <p>For check-in, directions, house rules, and stay coordination, contact support with your booking details.</p>
        </div>
      </div>

      <section>
        <h2>Contact support</h2>
        <div class="hv-static-callout">
          <p><strong>Email:</strong> <a href="mailto:info@homevilas.com">info@homevilas.com</a>, <a href="mailto:meena@homevilas.com">meena@homevilas.com</a></p>
          <p><strong>Call or WhatsApp:</strong> <a href="tel:+918287703118">+91 82877 03118</a></p>
          <p><strong>Office:</strong> E-6, Kalkaji, New Delhi-110019</p>
        </div>
      </section>
    `,
  },
  "partner-with-us": {
    title: "Partner With Us",
    excerpt:
      "Partner with Homevilas for villa distribution, B2B travel demand, curated packages, corporate stays, and hospitality growth.",
    html: `
      <section>
        <p class="eyebrow">Partnerships</p>
        <h2>Grow travel and hospitality business with <em>Homevilas</em></h2>
        <p>
          Homevilas works with property owners, travel companies, corporate partners, destination operators,
          and experience providers to create reliable stay and travel solutions across India.
        </p>
      </section>

      <div class="hv-static-grid hv-static-grid-2">
        <div class="hv-static-card">
          <h3>Property and inventory partners</h3>
          <p>List curated villas, homestays, boutique stays, and premium serviced inventory for qualified travel demand.</p>
        </div>
        <div class="hv-static-card">
          <h3>Travel and B2B partners</h3>
          <p>Collaborate on group bookings, agent distribution, corporate retreats, destination weddings, and packages.</p>
        </div>
        <div class="hv-static-card">
          <h3>Experience partners</h3>
          <p>Bundle local experiences, transport, meal plans, concierge services, and destination-led activities.</p>
        </div>
        <div class="hv-static-card">
          <h3>Brand collaborations</h3>
          <p>Build campaign, content, and hospitality collaborations around premium private-stay travel.</p>
        </div>
      </div>

      <section>
        <h2>What to share</h2>
        <ul>
          <li>Your company or property name</li>
          <li>City, destination, or operating region</li>
          <li>Partnership type and available inventory or services</li>
          <li>Primary contact name, phone number, and email address</li>
        </ul>
      </section>

      <section>
        <h2>Start a partnership conversation</h2>
        <div class="hv-static-callout">
          <p><strong>Email:</strong> <a href="mailto:info@homevilas.com">info@homevilas.com</a></p>
          <p><strong>Call or WhatsApp:</strong> <a href="tel:+918287703118">+91 82877 03118</a></p>
          <p><strong>B2B form:</strong> <a href="/b2b-registration">Open B2B Registration</a></p>
        </div>
      </section>
    `,
  },
  "b2b-registration": {
    title: "B2B Registration",
    excerpt:
      "Register your business interest with Homevilas for travel partnerships, corporate stays, group bookings, and distribution opportunities.",
    html: `
      <section>
        <p class="eyebrow">B2B</p>
        <h2>Register your business interest with <em>Homevilas</em></h2>
        <p>
          Share your business details with the Homevilas team for agent partnerships, corporate travel, group stays,
          inventory distribution, package collaborations, or destination services.
        </p>
      </section>

      <div class="hv-static-grid hv-static-grid-3">
        <div class="hv-static-card">
          <h3>Travel agents</h3>
          <p>Work with Homevilas for curated villas, homestays, packages, and group travel requirements.</p>
        </div>
        <div class="hv-static-card">
          <h3>Corporate enquiries</h3>
          <p>Plan team offsites, leadership retreats, long-stay requirements, and event-linked accommodation.</p>
        </div>
        <div class="hv-static-card">
          <h3>Hospitality partners</h3>
          <p>Connect property inventory, experiences, transport, and destination services with Homevilas demand.</p>
        </div>
      </div>

      <section>
        <h2>Send registration details</h2>
        <div class="hv-static-callout">
          <p><strong>Email:</strong> <a href="mailto:info@homevilas.com">info@homevilas.com</a></p>
          <p><strong>WhatsApp:</strong> <a href="https://wa.me/918287703118" target="_blank" rel="noreferrer">+91 82877 03118</a></p>
          <p>Include company name, GST details if applicable, city, partnership category, website or profile link, and contact person details.</p>
        </div>
      </section>
    `,
  },
  privacy: {
    title: "Privacy Policy",
    excerpt:
      "This policy explains how Homevilas collects, uses, shares, stores, and protects personal information across the platform.",
    html: `
      <section>
        <p class="eyebrow">Legal</p>
        <h2>Privacy <em>Policy</em></h2>
        <p>
          This Privacy Policy is issued by <strong>Northern Adventure Private Limited</strong>, operating as Homevilas.
          It explains how we collect, use, store, protect, and share personal information when you browse our platform,
          make enquiries, create accounts, or complete bookings.
        </p>
      </section>

      <div class="hv-static-callout">
        <p><strong>Entity:</strong> Northern Adventure Private Limited</p>
        <p><strong>Brand:</strong> Homevilas</p>
        <p><strong>Contact for privacy queries:</strong> <a href="mailto:info@homevilas.com">info@homevilas.com</a></p>
      </div>

      <section>
        <h2>1. Information we collect</h2>
        <ul>
          <li>Identity details such as your name and profile information</li>
          <li>Contact details such as email address, phone number, and address</li>
          <li>Booking data including dates, guest counts, special requests, and property preferences</li>
          <li>Payment and billing data necessary to process transactions through supported gateways</li>
          <li>Technical and usage information such as device data, browser details, IP address, and activity logs</li>
          <li>Communication records when you contact our team or interact with hosts through the platform</li>
        </ul>
      </section>

      <section>
        <h2>2. How we use your data</h2>
        <ul>
          <li>To create and manage accounts, enquiries, and bookings</li>
          <li>To process payments, confirmations, refunds, and travel coordination</li>
          <li>To share essential booking information with relevant hosts or service partners</li>
          <li>To provide customer support, fraud prevention, and service quality monitoring</li>
          <li>To improve the website, listings, and overall travel experience</li>
          <li>To comply with legal, accounting, tax, and regulatory obligations</li>
        </ul>
      </section>

      <section>
        <h2>3. Sharing of data</h2>
        <p>Homevilas does not sell personal data. We may share limited information only where necessary, including:</p>
        <ul>
          <li>With hosts or property managers to fulfill bookings and stays</li>
          <li>With payment processors and technology providers that support our services</li>
          <li>With legal or regulatory authorities when required by law</li>
          <li>With support, fraud-prevention, or infrastructure vendors under controlled access</li>
        </ul>
      </section>

      <section>
        <h2>4. Cookies and analytics</h2>
        <p>
          We may use cookies and related tools for session continuity, performance monitoring, analytics, and experience improvements.
          You can manage cookie preferences through your browser settings where supported.
        </p>
      </section>

      <section>
        <h2>5. Data retention and security</h2>
        <p>
          Personal data is retained only as long as reasonably necessary for bookings, support, financial records, security, and
          legal compliance. We use reasonable technical and organizational safeguards to protect stored data.
        </p>
      </section>

      <section>
        <h2>6. Your rights</h2>
        <ul>
          <li>Request access to the personal data we hold about you</li>
          <li>Request correction of inaccurate or incomplete data</li>
          <li>Request deletion where legally permissible</li>
          <li>Withdraw consent for optional communications</li>
          <li>Raise questions or complaints about how data is handled</li>
        </ul>
      </section>

      <section>
        <h2>7. Contact for privacy matters</h2>
        <div class="hv-static-card">
          <p><strong>Email:</strong> <a href="mailto:info@homevilas.com">info@homevilas.com</a></p>
          <p><strong>Address:</strong> E-6, Kalkaji, New Delhi-110019</p>
          <p><strong>Related page:</strong> <a href="/terms">Terms & Conditions</a></p>
        </div>
      </section>
    `,
  },
  terms: {
    title: "Terms & Conditions",
    excerpt:
      "These terms govern access to Homevilas, user accounts, bookings, payments, cancellations, host listings, and dispute handling.",
    html: `
      <section>
        <p class="eyebrow">Legal</p>
        <h2>Terms &amp; <em>Conditions</em></h2>
        <p>
          These Terms &amp; Conditions apply to your use of Homevilas and to bookings made through the platform operated by
          <strong> Northern Adventure Private Limited</strong>. By using Homevilas, you agree to these terms.
        </p>
      </section>

      <div class="hv-static-callout">
        <p><strong>Platform:</strong> Homevilas</p>
        <p><strong>Operating entity:</strong> Northern Adventure Private Limited</p>
        <p><strong>Registered office:</strong> E-6, Kalkaji, New Delhi-110019</p>
      </div>

      <section>
        <h2>1. Marketplace role</h2>
        <p>
          Homevilas operates as a hospitality marketplace and booking platform. Many listed properties are managed by independent
          third-party hosts or owners. Listing-level rules, occupancy limits, cancellation policies, and house rules may apply in
          addition to these terms.
        </p>
      </section>

      <section>
        <h2>2. Eligibility and accounts</h2>
        <ul>
          <li>You must be legally capable of entering into a binding arrangement to use the platform</li>
          <li>Account information provided by you must be accurate and current</li>
          <li>You are responsible for maintaining the confidentiality of your account access</li>
        </ul>
      </section>

      <section>
        <h2>3. Bookings and payments</h2>
        <ul>
          <li>Bookings are subject to availability, approval, and payment completion where applicable</li>
          <li>Pricing, taxes, fees, and cancellation terms may vary by property or package</li>
          <li>Payment processing may be handled through approved third-party gateways</li>
          <li>Special requests do not become binding unless confirmed through the relevant booking flow</li>
        </ul>
      </section>

      <section>
        <h2>4. Cancellations and refunds</h2>
        <p>
          Refund eligibility depends on the applicable cancellation policy shown during booking or on the listing. Some bookings
          may be partially refundable, non-refundable, or subject to date-based refund windows.
        </p>
      </section>

      <section>
        <h2>5. Guest responsibilities</h2>
        <ul>
          <li>Follow listing-specific property rules and occupancy limits</li>
          <li>Provide correct guest and stay information</li>
          <li>Use the property lawfully and responsibly</li>
          <li>Be liable for damage caused during the stay, where applicable</li>
        </ul>
      </section>

      <section>
        <h2>6. Host responsibilities</h2>
        <ul>
          <li>Maintain accurate listing data, availability, pricing, and property details</li>
          <li>Honor confirmed bookings except where cancellation is justified under policy</li>
          <li>Ensure reasonable standards of safety, cleanliness, and disclosure</li>
        </ul>
      </section>

      <section>
        <h2>7. Liability and platform limitations</h2>
        <p>
          Homevilas aims to facilitate a reliable experience, but it is not liable for every third-party act, omission, listing
          inaccuracy, or on-property incident beyond the limits allowed by law and the applicable booking arrangement.
        </p>
      </section>

      <section>
        <h2>8. Privacy and data</h2>
        <p>
          Use of the platform is also subject to our <a href="/privacy">Privacy Policy</a>, which explains data handling and related rights.
        </p>
      </section>

      <section>
        <h2>9. Governing law and disputes</h2>
        <p>
          These terms are governed by applicable Indian law. Disputes that cannot be resolved through support channels may be
          handled through the dispute process and jurisdiction applicable to the operating entity.
        </p>
      </section>

      <section>
        <h2>10. Contact</h2>
        <div class="hv-static-card">
          <p><strong>Email:</strong> <a href="mailto:info@homevilas.com">info@homevilas.com</a></p>
          <p><strong>Support:</strong> <a href="/contact">Contact Us</a></p>
        </div>
      </section>
    `,
  },
};

export const CORE_STATIC_SLUGS = Object.keys(STATIC_PAGE_CONTENT);
